import {useCallback, useEffect, useState} from 'react'

interface KnownBalance {
  subscriptionId: string
  units: number
}

interface LastKnownBalance {
  /** The query value, else the last value seen for this subscription, else `null`. */
  balance: number | null
  /** Record a value that did not come from the query (e.g. the chat agent's `balanceRemaining`). */
  remember: (units: number) => void
}

/**
 * `useBalance` drops its cache entry on any fetch error, which would turn a known empty balance back
 * into "unknown" and silently unlock the chat composer. Holding on to the last value seen for the
 * same subscription keeps the lock until a fetch actually succeeds with a new number.
 */
export function useLastKnownBalance(
  subscriptionId: string | null,
  queryBalance: number | undefined
): LastKnownBalance {
  const [known, setKnown] = useState<KnownBalance | null>(null)

  useEffect(() => {
    if (subscriptionId && queryBalance !== undefined) {
      setKnown({subscriptionId, units: queryBalance})
    }
  }, [subscriptionId, queryBalance])

  const remember = useCallback(
    (units: number) => {
      if (subscriptionId) setKnown({subscriptionId, units})
    },
    [subscriptionId]
  )

  const fallback = known && known.subscriptionId === subscriptionId ? known.units : null
  return {balance: queryBalance ?? fallback, remember}
}
